import { ExternalLink } from "lucide-react"

/**
 * A map of one location, from OpenStreetMap.
 *
 * Deliberately not Google's embed. Google's iframe sets its NID tracking
 * cookie and reports the visitor's IP to Google on page load, before anyone
 * has agreed to anything — which under GDPR/PECR is what a consent banner
 * exists to cover. Both of these sites are cookieless on purpose (it is why
 * Pulse replaced Google Analytics and why neither brand carries a banner), and
 * a mental-health site is the last place to start tracking people silently to
 * save a day's work. OpenStreetMap's embed sets no cookies at all.
 *
 * The trade is Street View and the familiar Google interface, so the Google
 * link stays, prominently, right under the map: people navigate with the app
 * they already have, and that is a click they make deliberately.
 */

interface LocationMapProps {
  latitude: number
  longitude: number
  /** Named in the iframe title and the directions link, e.g. "Hebden Bridge". */
  name: string
  /** The location's Google Maps link, if it has one. */
  mapsUrl?: string | null
  mapLabel: string
  directionsLabel: string
  className?: string
}

/** Roughly 500m of context around the pin — enough to place it on its street. */
const SPAN = 0.005

export function LocationMap({
  latitude,
  longitude,
  name,
  mapsUrl,
  mapLabel,
  directionsLabel,
  className = "",
}: LocationMapProps) {
  const bbox = [longitude - SPAN, latitude - SPAN / 2, longitude + SPAN, latitude + SPAN / 2]
    .map((n) => n.toFixed(6))
    .join(",")
  const src =
    `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}` +
    `&layer=mapnik&marker=${latitude.toFixed(6)},${longitude.toFixed(6)}`

  return (
    <div className={`flex flex-col ${className}`}>
      <iframe
        src={src}
        title={mapLabel.replace("%{name}", name)}
        loading="lazy"
        /* No referrer: the tile server needs the coordinates to draw the map,
           not the page the reader came from. */
        referrerPolicy="no-referrer"
        className="min-h-72 w-full flex-1 rounded-2xl border-0 ring-1 ring-hairline"
      />
      {mapsUrl && (
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl border border-hairline bg-cream px-5 py-3 text-sm font-medium text-teal-mid transition-colors hover:bg-stone"
        >
          <ExternalLink className="h-4 w-4" />
          {directionsLabel}
        </a>
      )}
    </div>
  )
}

export default LocationMap
